/**
 * Fiscal calendar — `/ledger/fiscal-periods`.
 *
 * A period's status is posting authority, so every change here is a
 * compare-and-set: the request carries the status the screen was showing as
 * `expected_status`, and the server refuses if someone else moved it first.
 * That is why the buttons are derived from the row's current status rather
 * than being a free choice of target.
 *
 * The reason is a controlled code, not free text — reopening a closed period
 * is recorded as an authorized reopen, and there is no box to write an excuse
 * in. Period 13 is calendar metadata, not posting authority, and is labelled
 * as an adjustment period so nobody mistakes it for a thirteenth month.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarPlus } from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from "@erp/ui";

import { formatCalendarDate } from "@/lib/format";
import { newIdempotencyKey } from "@/features/shared/ops-api";

import {
  TRANSITIONS,
  generateFiscalYear,
  listFiscalYears,
  transitionFiscalPeriod,
  type FiscalPeriod,
  type FiscalPeriodStatus,
  type FiscalYear,
} from "../calendar-api";
import { ledgerKeys } from "../queries";

const STATUS_VARIANTS: Record<FiscalPeriodStatus, "success" | "secondary" | "outline"> = {
  open: "success",
  soft_closed: "secondary",
  locked: "outline",
};

const YEAR_CODE = /^FY\d{4}$/;

export interface FiscalCalendarPageProps {
  canTransition?: boolean;
  canGenerate?: boolean;
}

export function FiscalCalendarPage({
  canTransition = false,
  canGenerate = false,
}: FiscalCalendarPageProps) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [startsOn, setStartsOn] = useState("");

  const years = useQuery<FiscalYear[]>({
    queryKey: ledgerKeys.fiscalYears.all,
    queryFn: ({ signal }) => listFiscalYears(signal),
  });

  const day = (iso: string): string => {
    try {
      return formatCalendarDate(iso, i18n.language);
    } catch {
      return iso;
    }
  };

  const move = async (period: FiscalPeriod, target: FiscalPeriodStatus) => {
    const transition = TRANSITIONS.find(
      (candidate) => candidate.from === period.status && candidate.to === target,
    );
    if (transition === undefined) return;
    setBusyId(period.id);
    setError(null);
    try {
      await transitionFiscalPeriod(
        period.id,
        {
          // The compare half: the status this screen was showing.
          expected_status: period.status,
          target_status: target,
          reason_code: transition.reason,
        },
        newIdempotencyKey(),
      );
      await queryClient.invalidateQueries({ queryKey: ledgerKeys.fiscalYears.all });
    } catch {
      setError(
        t(
          "ledger.fiscalCalendar.transitionError",
          "The period could not be moved. Reload — someone may have changed it first.",
        ),
      );
    } finally {
      setBusyId(null);
    }
  };

  const generate = async () => {
    setError(null);
    try {
      await generateFiscalYear(
        { code: code.toUpperCase(), starts_on: startsOn },
        newIdempotencyKey(),
      );
      await queryClient.invalidateQueries({ queryKey: ledgerKeys.fiscalYears.all });
      setOpen(false);
      setCode("");
      setStartsOn("");
    } catch {
      setError(t("ledger.fiscalCalendar.generateError", "The year could not be generated."));
    }
  };

  const rows = years.data ?? [];

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-xl font-semibold tracking-tight">
            {t("ledger.fiscalCalendar.title", "Fiscal calendar")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t(
              "ledger.fiscalCalendar.subtitle",
              "Which periods accept postings, and which are closed to them.",
            )}
          </p>
        </div>
        {canGenerate ? (
          <Button type="button" onClick={() => setOpen(true)}>
            <CalendarPlus className="size-4" aria-hidden="true" />
            {t("ledger.fiscalCalendar.generate", "Generate a year")}
          </Button>
        ) : null}
      </div>

      {error === null ? null : (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}

      {years.isLoading ? (
        <p className="text-sm text-muted-foreground">{t("common.loading", "Loading…")}</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {t("ledger.fiscalCalendar.empty", "No fiscal year has been generated yet.")}
        </p>
      ) : (
        rows.map((year) => (
          <Card key={year.id}>
            <CardHeader>
              <CardTitle>
                <h2 className="flex flex-wrap items-baseline gap-2 text-base">
                  <span dir="ltr" className="font-mono">
                    {year.code}
                  </span>
                  <span className="text-sm font-normal text-muted-foreground">
                    {day(year.starts_on)} – {day(year.ends_on)}
                  </span>
                </h2>
              </CardTitle>
            </CardHeader>
            <CardContent className="overflow-x-auto p-0">
              <table className="w-full min-w-[44rem] text-sm">
                <thead>
                  <tr className="border-b text-xs uppercase text-muted-foreground">
                    <th scope="col" className="p-3 text-start">
                      {t("ledger.fiscalCalendar.period", "Period")}
                    </th>
                    <th scope="col" className="p-3 text-start">
                      {t("ledger.fiscalCalendar.range", "Dates")}
                    </th>
                    <th scope="col" className="p-3 text-start">
                      {t("ledger.fiscalCalendar.status", "Status")}
                    </th>
                    {canTransition ? <th scope="col" className="p-3" /> : null}
                  </tr>
                </thead>
                <tbody>
                  {year.periods.map((period) => (
                    <tr key={period.id} className="border-b last:border-0">
                      <td className="p-3">
                        {period.name}
                        {period.is_adjustment ? (
                          <Badge variant="outline" className="ms-2 text-[10px]">
                            {t("ledger.fiscalCalendar.adjustment", "Adjustment")}
                          </Badge>
                        ) : null}
                      </td>
                      <td className="p-3 text-muted-foreground">
                        {day(period.starts_on)} – {day(period.ends_on)}
                      </td>
                      <td className="p-3">
                        <Badge variant={STATUS_VARIANTS[period.status]}>
                          {t(`ledger.fiscalStatus.${period.status}`, period.status)}
                        </Badge>
                      </td>
                      {canTransition ? (
                        <td className="p-3 text-end">
                          <div className="flex flex-wrap justify-end gap-2">
                            {TRANSITIONS.filter(
                              (transition) => transition.from === period.status,
                            ).map((transition) => (
                              <Button
                                key={transition.to}
                                type="button"
                                variant="outline"
                                size="sm"
                                disabled={busyId === period.id}
                                onClick={() => void move(period, transition.to)}
                              >
                                {t(
                                  `ledger.fiscalAction.${transition.from}_${transition.to}`,
                                  transition.to,
                                )}
                              </Button>
                            ))}
                          </div>
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        ))
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("ledger.fiscalCalendar.generate", "Generate a year")}</DialogTitle>
            <DialogDescription>
              {t(
                "ledger.fiscalCalendar.generateHint",
                "The server lays out the twelve periods and the adjustment period from the start date.",
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="fy-code">{t("ledger.fiscalCalendar.code", "Code")}</Label>
              <Input
                id="fy-code"
                dir="ltr"
                className="font-mono uppercase"
                placeholder="FY2027"
                value={code}
                onChange={(event) => setCode(event.target.value.toUpperCase())}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="fy-start">{t("ledger.fiscalCalendar.startsOn", "Starts on")}</Label>
              <Input
                id="fy-start"
                type="date"
                value={startsOn}
                onChange={(event) => setStartsOn(event.target.value)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              {t("actions.cancel", "Cancel")}
            </Button>
            <Button
              type="button"
              disabled={!YEAR_CODE.test(code) || startsOn === ""}
              onClick={() => void generate()}
            >
              {t("actions.save", "Save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
